import style from './style.module.scss'
import classnames from 'classnames'
import { useState, useRef, useContext, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { decamelizeKeys } from 'humps'
import { Button, Text } from '@crossfit/design-system'
import PageHeader from 'components/Admin/Shared/PageHeader'
import Container from 'components/Admin/Shared/Container'
import Display from 'components/Admin/Shared/Container/Row/Display'
import Coaches from 'components/Admin/Coaches'
import ImageModal, {
  AffiliateAdminPhotoType,
  useCroppingMachine,
} from 'components/Admin/DirectoryPage/ImageModal'
import { BioForm } from 'components/Admin/ContactInfo/forms/Bio'
import { updateAffiliateAttribute } from 'store/slices/affiliate'
import { ToggleValue } from 'components/Admin/Shared/Toggle'
import ImageView from 'components/Admin/DirectoryPage/ImageView'
import {
  patchAffiliate,
  patchAffiliateGalleryImagesOrdering,
} from 'services/affiliates'
import { useFormNotification } from 'hooks/useFormNotification'
import { GalleryImages } from 'services/affiliates'
import { DirectoryPageContext } from 'contexts/DirectoryPage'
import { useAffiliate } from 'hooks/useAffiliate'
import { Icon, Loader } from '@crossfit/ui'
import LogoView from 'components/Admin/DirectoryPage/LogoView'
import { useIsMobile } from 'hooks/useMediaQuery'
import SelectionModal from 'components/SelectionModal'
import { uploadAffiliatePhoto } from 'services/affiliates/imageService/upload'
import { useAuth } from 'components/Auth'
import ImageCropper from 'components/ImageCroppper'
import { Link, useNavigate } from 'react-router-dom'
import ScheduleGuide from 'components/ScheduleGuide'

export default function DirectoryPage() {
  const isMobile = useIsMobile()
  const { user } = useAuth()
  const { selectedAffiliate: affiliateDetail } = useAffiliate()
  const { directoryPage } = useContext(DirectoryPageContext)
  const [isEditingBio, setIsEditingBio] = useState(false)
  const basicInfoSectionRef = useRef<HTMLElement>()
  const highlightsSectionRef = useRef<HTMLElement>()
  const bioSectionRef = useRef<HTMLElement>()
  const photosSectionRef = useRef<HTMLElement>()
  const coachesSectionRef = useRef<HTMLElement>()
  const scheduleSectionRef = useRef<HTMLElement>()
  const [primaryImageModalOpen, setPrimaryImageModalOpen] =
    useState<boolean>(false)
  const [galleryImageModalOpen, setGalleryImageModalOpen] =
    useState<boolean>(false)
  const [scheduleImageModalOpen, setScheduleImageModalOpen] =
    useState<boolean>(false)
  const [showBottomModal, setShowBottomModal] = useState<boolean>(false)
  const galleryImages = directoryPage.galleryImages.get.value
  const [isReordering, setIsReordering] = useState<boolean>(false)
  const [dragItemIndex, setDragItemIndex] = useState<number>(null)
  const [dragOverItemIndex, setDragOverItemIndex] = useState<number>(null)
  const handleFormNotification = useFormNotification()
  const dispatch = useDispatch()
  const [logoImageModalOpen, setLogoImageModalOpen] = useState<boolean>(false)
  const [isUploading, setIsUploading] = useState(false)
  const [showImageCropper, setShowImageCropper] = useState(false)
  const [preCropImage, setPreCropImage] = useState<File>(null)
  const [showScheduleGuide, setShowScheduleGuide] = useState(false)
  const navigate = useNavigate()

  const updateSettings = async (newSettings: any) => {
    const settings = decamelizeKeys(newSettings)
    try {
      await patchAffiliate(affiliateDetail.id, {
        settings: {
          ...settings,
        },
      })

      handleFormNotification('success')
    } catch {
      handleFormNotification('error', { message: 'Something went wrong' })
    }
  }

  const handleGallerySort = (e) => {
    e.preventDefault()
    const galleryItems = [...galleryImages]
    const draggedItemContent = galleryItems.splice(dragItemIndex, 1)[0]
    galleryItems.splice(dragOverItemIndex, 0, draggedItemContent)

    setDragItemIndex(null)
    setDragOverItemIndex(null)
    directoryPage.galleryImages.get.updateFn(galleryItems)
  }

  const onGallerySortCancel = () => {
    directoryPage.galleryImages.get.updateFn(affiliateDetail.images?.gallery)
    setIsReordering(false)
  }

  const onGallerySortSave = async () => {
    const galleryImagesOrdered: GalleryImages = galleryImages.reduce(
      (accumulator, currentValue, currentIndex) => {
        accumulator.push({ url: currentValue.url, order: currentIndex })
        return accumulator
      },
      [],
    )

    const response = await patchAffiliateGalleryImagesOrdering(
      affiliateDetail.id,
      galleryImagesOrdered,
    )

    if (response.status < 300) {
      directoryPage.galleryImages.get.updateFn(galleryImagesOrdered)
      dispatch(
        updateAffiliateAttribute({
          ...affiliateDetail,
          images: {
            ...affiliateDetail.images,
            gallery: galleryImagesOrdered,
          },
        }),
      )
      handleFormNotification('success')
      setIsReordering(false)
    } else {
      handleFormNotification('error', { message: 'Something went wrong' })
    }
  }

  const addOrChangeImageButtonClicked = () => {
    if (isMobile) {
      setShowBottomModal(true)
    } else {
      setScheduleImageModalOpen(true)
    }
  }

  const onMobileFileSelected = async (files: FileList) => {
    if (files.length) {
      const file = files[0]
      setShowBottomModal(false)
      setPreCropImage(file)
      setShowImageCropper(true)
    }
  }

  const imageCropped = async (croppedImageFile: File) => {
    setShowImageCropper(false)
    setIsUploading(true)
    const affiliateImages = await uploadAffiliatePhoto(
      'schedule',
      affiliateDetail?.id,
      croppedImageFile,
      user?.id,
      (pe) => {
        // console.log(pe)
      },
    )
    directoryPage.scheduleImageUrl.get.updateFn(affiliateImages?.schedule?.url)
    setIsUploading(false)
    handleFormNotification('success')
  }

  const CustomScheduleHeader = () => {
    return (
      <div>
        <p className={style.scheduleGuideText}>
          Add an image displaying the class schedule at your affiliate. View our{' '}
          <Link
            to={'/admin/directory-page?schedule_guide=true'}
            className={style.scheduleGuideLink}
          >
            schedule guide
          </Link>
        </p>
      </div>
    )
  }

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const modalParam = queryParams.get('schedule_guide')

    console.log(modalParam)

    if (modalParam === 'true') {
      setShowScheduleGuide(true)
    } else {
      setShowScheduleGuide(false)
    }
  }, [location.href])

  return (
    <>
      {showBottomModal && (
        <SelectionModal
          onClose={() => setShowBottomModal(false)}
          onSelect={onMobileFileSelected}
        />
      )}
      {showImageCropper && (
        <ImageCropper
          imageFile={preCropImage}
          isOpen={showImageCropper}
          close={() => setShowImageCropper(false)}
          imageCropped={imageCropped}
        />
      )}
      {showScheduleGuide && (
        <ScheduleGuide
          close={() => {
            const queryParams = new URLSearchParams(location.search)
            queryParams.delete('schedule_guide')
            setShowScheduleGuide(false)
            navigate('/admin/directory-page')
          }}
        />
      )}
      <PageHeader
        title=''
        onBasicInfoClick={() => {
          window.scroll({
            top:
              basicInfoSectionRef.current.getBoundingClientRect().top +
              window.scrollY -
              200, // 100 is the offset from the top
            behavior: 'smooth',
          })
        }}
        onHighlightsClick={() => {
          window.scroll({
            top:
              highlightsSectionRef.current.getBoundingClientRect().top +
              window.scrollY -
              200, // 100 is the offset from the top
            behavior: 'smooth',
          })
        }}
        onBioClick={() => {
          window.scroll({
            top:
              bioSectionRef.current.getBoundingClientRect().top +
              window.scrollY -
              200, // 100 is the offset from the top
            behavior: 'smooth',
          })
        }}
        onPhotosClick={() => {
          window.scroll({
            top:
              bioSectionRef.current.getBoundingClientRect().top +
              window.scrollY -
              0, // 100 is the offset from the top
            behavior: 'smooth',
          })
        }}
        onCoachesClick={() => {
          window.scroll({
            top:
              coachesSectionRef.current.getBoundingClientRect().top +
              window.scrollY -
              200, // 100 is the offset from the top
            behavior: 'smooth',
          })
        }}
      />
      <section ref={basicInfoSectionRef}>
        <Container
          header='Basic Info'
          subHeader={`We recommend sharing all information below for visitors to contact your gym. Edit or update information on the <a href='/tools/admin/contact-info'>contact info</a> page.`}
        >
          <Display
            label='Contact Email'
            desc='Contact form submissions from visitors viewing your public profile will be sent to this email'
            value={directoryPage.email.get.value}
            isEditable={false}
            includeToggle
            toggleValue={directoryPage.email.get.toggleValue}
            onToggle={(state) => {
              updateSettings({
                contactEmail: {
                  isPublic: state === ToggleValue.PUBLIC ? true : false,
                },
              })
              directoryPage.email.get.toggleFn(state)
            }}
          />
          <Display
            label='Website'
            desc=''
            value={directoryPage.website.get.value}
            includeToggle={true}
            toggleValue={directoryPage.website.get.toggleValue}
            isEditable={false}
            onToggle={(state) => {
              updateSettings({
                web: {
                  isPublic: state === ToggleValue.PUBLIC ? true : false,
                },
              })
              directoryPage.website.get.toggleFn(state)
            }}
          />

          <Display
            label='Address'
            desc={directoryPage.address.get.description}
            toggleValue={directoryPage.address.get.toggleValue}
            onToggle={(state) => {
              updateSettings({
                address: {
                  isPublic: state === ToggleValue.PUBLIC ? true : false,
                },
              })
              directoryPage.address.get.toggleFn(state)
            }}
            value={directoryPage.address.get.value}
            includeToggle={true}
            isEditable={false}
          />
          <Display
            label='Phone'
            desc=''
            value={directoryPage.phone.get.value}
            includeToggle={true}
            isEditable={false}
            toggleValue={directoryPage.phone.get.toggleValue}
            onToggle={(state) => {
              updateSettings({
                contactPhone: {
                  isPublic: state === ToggleValue.PUBLIC ? true : false,
                },
              })
              directoryPage.phone.get.toggleFn(state)
            }}
          />
        </Container>
      </section>
      <section ref={scheduleSectionRef}>
        <Container
          header='Schedule'
          subHeaderComponent={<CustomScheduleHeader />}
        >
          <div className={style.customRow}>
            <div className={style.customRowChild}>
              <Text as='label' type='body' size='sm'>
                Class Schedule
              </Text>
            </div>
            <div className={style.customRowChild}>
              {directoryPage.scheduleImageUrl?.get?.value ? (
                isUploading ? (
                  <Loader />
                ) : (
                  <ImageView
                    type='schedule'
                    imageUrl={directoryPage.scheduleImageUrl.get.value}
                    setScheduleImage={
                      directoryPage.scheduleImageUrl.get.updateFn
                    }
                  />
                )
              ) : (
                <div className={style.emptySchedule}>
                  <div className={style.emptyScheduleContainer}>
                    <Text
                      type='body'
                      size='sm'
                      className={style.noScheduleText}
                    >
                      No Schedule Uploaded
                    </Text>
                  </div>
                  <div
                    className={style.noScheduleInfo}
                    onClick={() => setShowScheduleGuide(true)}
                  >
                    <Icon name='info' className={style.noScheduleIcon} />
                    <Text type='body' size='sm'>
                      How do I create a schedule to upload?
                    </Text>
                  </div>
                </div>
              )}
            </div>
            <div className={style.customRowChild}>
              <Button
                onClick={addOrChangeImageButtonClicked}
                variant='secondary'
                color='white'
              >
                {directoryPage.scheduleImageUrl.get.value
                  ? 'Change Image'
                  : 'Add Image'}
              </Button>
              <Text
                as='p'
                type='body'
                size='sm'
                className={style.imageUploadDesc}
              >
                Files must be a JPEG or PNG and no larger than 5 MB
              </Text>
            </div>
            {scheduleImageModalOpen && (
              <ImageModal
                type='schedule'
                affiliateId={affiliateDetail?.id}
                setImageUrl={directoryPage.scheduleImageUrl.get.updateFn}
                isOpen={scheduleImageModalOpen}
                setIsOpen={setScheduleImageModalOpen}
              />
            )}
          </div>
        </Container>
      </section>
      <section ref={highlightsSectionRef}>
        <Container
          header='Highlights'
          subHeader='Control what highlights you would like publicly visible.'
        >
          <Display
            label='Coach & Staff Credentials'
            desc=''
            includeToggle={true}
            coaches={directoryPage.coachCredentials.get.value}
            toggleValue={directoryPage.coachCredentials.get.toggleValue}
            onToggle={(state) => {
              updateSettings({
                certifications: {
                  isPublic: state === ToggleValue.PUBLIC ? true : false,
                },
              })
              directoryPage.coachCredentials.get.toggleFn(state)
            }}
          />
          <Display
            label='Years Affiliated'
            desc=''
            includeToggle
            yearsAffiliated={directoryPage.yearsAffiliated.get.value}
            toggleValue={directoryPage.yearsAffiliated.get.toggleValue}
            onToggle={(state) => {
              updateSettings({
                yearsAffiliated: {
                  isPublic: state === ToggleValue.PUBLIC ? true : false,
                },
              })
              directoryPage.yearsAffiliated.get.toggleFn(state)
            }}
          />
        </Container>
      </section>
      <section ref={bioSectionRef}>
        <Container header='Bio' subHeader=''>
          {isEditingBio ? (
            <BioForm
              affiliate={affiliateDetail}
              setIsEditing={setIsEditingBio}
              update={directoryPage.bio.get.updateFn}
            />
          ) : (
            <Display
              label='About'
              desc=''
              value={directoryPage.bio.get.value}
              setIsEditing={setIsEditingBio}
              includeValueCharacterCount={true}
              toggleValue={directoryPage.bio.get.toggleValue}
              isEditable
            />
          )}
        </Container>
      </section>
      <section ref={photosSectionRef}>
        <Container
          header='Photos'
          subHeader='Primary photos will be shown as the image for your affiliate on the CrossFit Map. The ideal aspect ratio is 16:9 and should show a recognizable image of your gym to the public. The gallery photos will be shown in your Directory Page and should also have an aspect ratio of 16:9.'
        >
          <div className={style.customRow}>
            <div className={style.customRowChild}>
              <Text as='label' type='body' size='sm'>
                Gym Logo
              </Text>
            </div>
            <div className={style.customRowChild}>
              <LogoView imageUrl={directoryPage.logoImageUrl?.get?.value} />
            </div>
            <div className={style.customRowChild}>
              <Button
                onClick={() => setLogoImageModalOpen(true)}
                variant='secondary'
                color='white'
              >
                {directoryPage.logoImageUrl?.get?.value
                  ? 'Replace logo'
                  : 'Add logo'}
              </Button>
              <Text
                as='p'
                type='body'
                size='sm'
                className={style.imageUploadDesc}
              >
                Files must be a JPEG or PNG and no larger than 5 MB. Photos may
                take 5-10 minutes to reflect on the CrossFit Map.
              </Text>
            </div>
            {logoImageModalOpen && (
              <ImageModal
                type='logo'
                affiliateId={affiliateDetail.id}
                setImageUrl={directoryPage.logoImageUrl.get.updateFn}
                isOpen={logoImageModalOpen}
                setIsOpen={setLogoImageModalOpen}
              />
            )}
          </div>
          <div className={style.customRow}>
            <div className={style.customRowChild}>
              <Text as='label' type='body' size='sm'>
                Primary Photo
              </Text>
            </div>
            <div className={style.customRowChild}>
              <ImageView
                type='primary'
                imageUrl={directoryPage.primaryImageUrl.get.value}
                setPrimaryImage={directoryPage.primaryImageUrl.get.updateFn}
              />
            </div>
            <div className={style.customRowChild}>
              <Button
                onClick={() => setPrimaryImageModalOpen(true)}
                variant='secondary'
                color='white'
              >
                {directoryPage.primaryImageUrl.get.value?.includes(
                  'wp-content/uploads/affiliates/default-profile-photo.png',
                )
                  ? 'Add image'
                  : 'Change image'}
              </Button>
              <Text
                as='p'
                type='body'
                size='sm'
                className={style.imageUploadDesc}
              >
                Files must be a JPEG or PNG and no larger than 3 MB. Photos may
                take 5-10 minutes to reflect on the CrossFit Map.
              </Text>
            </div>
            {primaryImageModalOpen && (
              <ImageModal
                type='primary'
                affiliateId={affiliateDetail.id}
                setImageUrl={directoryPage.primaryImageUrl.get.updateFn}
                isOpen={primaryImageModalOpen}
                setIsOpen={setPrimaryImageModalOpen}
              />
            )}
          </div>
          <div className={style.customRow}>
            <div className={style.customRowChild}>
              <Text as='label' type='body' size='sm'>
                Gallery Photos
              </Text>
            </div>
            <div className={style.customRowChild}>
              {galleryImages && galleryImages.length ? (
                <div
                  className={classnames(
                    style.imageGroup,
                    isReordering && style.dragging,
                  )}
                >
                  {isReordering
                    ? galleryImages.map((image, i) => (
                        <div
                          key={`image-wrapper-reorder-${i}`}
                          className={classnames(
                            style.imageWrapper,
                            style.dragging,
                            dragItemIndex === i && style.dragItem,
                            dragOverItemIndex === i && style.dragOverItem,
                          )}
                          draggable
                          onDragStart={() => setDragItemIndex(i)}
                          onDragEnter={() => setDragOverItemIndex(i)}
                          onDragEnd={(e) => handleGallerySort(e)}
                          onDragOver={(e) => e.preventDefault}
                        >
                          <ImageView
                            type='gallery'
                            imageUrl={image.url}
                            setGalleryImages={
                              directoryPage.galleryImages.get.updateFn
                            }
                            isReordering
                          />
                        </div>
                      ))
                    : galleryImages.map((image, i) => (
                        <ImageView
                          key={`image-wrapper-${i}`}
                          type='gallery'
                          imageUrl={image.url}
                          setGalleryImages={
                            directoryPage.galleryImages.get.updateFn
                          }
                        />
                      ))}
                </div>
              ) : null}
            </div>
            <div className={style.customRowChild}>
              {galleryImages && galleryImages.length > 15 ? null : (
                <Button
                  onClick={() => setGalleryImageModalOpen(true)}
                  variant='secondary'
                  color='white'
                >
                  Add Image
                </Button>
              )}
              {galleryImages?.length > 1 ? (
                <Button
                  onClick={() => setIsReordering((s) => !s)}
                  variant='secondary'
                  color='white'
                >
                  Change Order
                </Button>
              ) : null}
              <Text
                as='p'
                type='body'
                size='sm'
                className={style.imageUploadDesc}
              >
                The max images you may upload is 15. Files must be a JPEG or PNG
                and no larger than 5 MB.
              </Text>
            </div>
            {galleryImageModalOpen && (
              <ImageModal
                type='gallery'
                affiliateId={affiliateDetail.id}
                setGalleryImages={directoryPage.galleryImages.get.updateFn}
                isOpen={galleryImageModalOpen}
                setIsOpen={setGalleryImageModalOpen}
              />
            )}
          </div>
          <div
            className={classnames(
              style.galleryReorderFormControls,
              isReordering && style.show,
            )}
          >
            <Button
              variant='secondary'
              color='white'
              size='sm'
              onClick={onGallerySortCancel}
            >
              Cancel
            </Button>
            <Button variant='primary' size='sm' onClick={onGallerySortSave}>
              Save
            </Button>
          </div>
        </Container>
      </section>
      <section ref={coachesSectionRef}>
        <Container
          header='Coaches & Staff'
          subHeader={`To add or remove staff visit the <a href='/tools/admin/staff'>coaches & staff</a> page.`}
        >
          <Coaches />
        </Container>
      </section>
    </>
  )
}
